import { enforceExhaustive } from "@mirage/growth/util";
import { callApiV2 } from "@mirage/service-dbx-api";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useCallback, useReducer, useState } from "react";
import { DashInviteFormSource } from "../types";
import { isValidInvitee } from "../utils/isValidInvitee";
import { useDashTeamInvitePapLogger } from "./useDashTeamInvitePapLogger";

import { type DropboxResponseError } from "@dropbox/api-v2-client";
import type { dash_growth } from "@dropbox/api-v2-client/types/dropbox_types";
import { PAP_Send_DashInviteModal } from "@mirage/analytics/events/types/send_dash_invite_modal";
import type {
  IndividualInviteFormContact,
  InviteFormContact,
  InviteWholeTeamFormContact,
} from "../types";

// Submission status constants
export const SUBMISSION_STATUS_SUBMITTING = "submitting";
export const SUBMISSION_STATUS_SUBMITTED = "submitted";

export interface InviteFormSubmissionResult {
  successfullyInvitedEmails: string[];
  successfullySuggestedEmails: string[];
  attemptedEmails?: string[];
  failedEmails: string[];
  error?: dash_growth.InviteToDashTeamError;
}

type SubmissionStatus =
  | typeof SUBMISSION_STATUS_SUBMITTING
  | typeof SUBMISSION_STATUS_SUBMITTED
  | null;

type UseSubmitReturnType = {
  handleSubmit: () => void;
  submissionStatus: SubmissionStatus;
  submissionResult: InviteFormSubmissionResult;
};

const initialState: InviteFormSubmissionResult = {
  successfullyInvitedEmails: [],
  successfullySuggestedEmails: [],
  attemptedEmails: [],
  failedEmails: [],
  error: undefined,
};

type SubmitInviteFormAction =
  | { type: "reset" }
  | {
      type: "attempt";
      payload: Pick<InviteFormSubmissionResult, "attemptedEmails">;
    }
  | {
      type: "fail";
      payload?: {
        error: dash_growth.InviteToDashTeamError;
      };
    }
  | {
      type: "receive_response";
      payload: Omit<InviteFormSubmissionResult, "attemptedEmails">;
    };

export const parseInviteUsersResponseInviteResult = (
  inviteResult: dash_growth.DashTeamInviteeResult[],
): Omit<InviteFormSubmissionResult, "attemptedEmails"> =>
  inviteResult.reduce<Omit<InviteFormSubmissionResult, "attemptedEmails">>(
    (acc, invitee) => {
      if (invitee.email) {
        if (
          invitee.invite_result?.[".tag"] &&
          ["invite_result_invited", "invite_result_invited_async"].includes(
            invitee.invite_result?.[".tag"],
          )
        ) {
          acc.successfullyInvitedEmails.push(invitee.email);
        } else if (
          invitee.invite_result?.[".tag"] === "invite_result_suggested"
        ) {
          acc.successfullySuggestedEmails.push(invitee.email);
        } else {
          acc.failedEmails.push(invitee.email);
        }
      }
      return acc;
    },
    {
      successfullyInvitedEmails: [],
      successfullySuggestedEmails: [],
      failedEmails: [],
    },
  );

const submitInviteFormReducer = (
  state: InviteFormSubmissionResult,
  action: SubmitInviteFormAction,
): InviteFormSubmissionResult => {
  switch (action.type) {
    case "reset":
      return initialState;
    case "attempt":
      return {
        ...state,
        // FYI: while API is still returning hardcoded data, this will only be the set of emails that are requested via the UI, not the hardcoded ones
        attemptedEmails: action.payload.attemptedEmails,
        error: undefined,
      };
    case "fail":
      return {
        ...initialState,
        attemptedEmails: state.attemptedEmails ? state.attemptedEmails : [],
        failedEmails: state.attemptedEmails ? state.attemptedEmails : [],
        error: action.payload?.error,
      };
    case "receive_response":
      return {
        ...state,
        successfullyInvitedEmails: action.payload.successfullyInvitedEmails,
        successfullySuggestedEmails: action.payload.successfullySuggestedEmails,
        failedEmails: action.payload.failedEmails,
        error: undefined,
      };
  }
};

export const useSubmitInviteForm = ({
  canAssignRoles,
  customMessage,
  dashInviteFormSource,
  individualContacts,
  teamContacts,
  isValidForm,
}: {
  canAssignRoles: boolean;
  customMessage?: string;
  dashInviteFormSource: DashInviteFormSource;
  individualContacts: Readonly<IndividualInviteFormContact[]>;
  teamContacts: Readonly<InviteWholeTeamFormContact[]>;
  isValidForm: boolean;
}): UseSubmitReturnType => {
  const [state, dispatch] = useReducer(submitInviteFormReducer, initialState);
  const [submissionStatus, setSubmissionStatus] =
    useState<SubmissionStatus>(null);
  const { logDashTeamInvitePapEvent } = useDashTeamInvitePapLogger();

  let inviteSource: dash_growth.DashInviteSource;
  switch (dashInviteFormSource) {
    case DashInviteFormSource.DASH_HOMEPAGE_MODAL:
      inviteSource = {
        ".tag": "invite_source_dash_homepage_modal",
      };
      break;
    case DashInviteFormSource.DASH_NEW_TEAM_ONBOARDING:
      inviteSource = {
        ".tag": "invite_source_dash_new_team_onboarding",
      };
      break;
    default:
      enforceExhaustive(dashInviteFormSource);
  }

  let dashInvitePlatform: dash_growth.DashInvitePlatform;
  switch (EnvCtx.surface) {
    case "web":
      dashInvitePlatform = {
        ".tag": "invite_platform_web",
      };
      break;
    case "desktop":
      dashInvitePlatform = {
        ".tag": "invite_platform_desktop",
      };
      break;
    default:
      dashInvitePlatform = {
        ".tag": "other",
      };
      break;
  }

  const handleSubmit = useCallback(() => {
    if (!isValidForm || !(submissionStatus === null)) {
      return;
    }
    dispatch({ type: "reset" });
    const validInvitees: (InviteFormContact & { email: string })[] = [
      ...individualContacts,
      ...teamContacts,
    ].filter(isValidInvitee);

    const requestInvitees = validInvitees.map((contact) => {
      const roleType: dash_growth.DashTeamRoleType | undefined = contact.newRole
        ? {
            ".tag":
              contact.newRole === "Co-admin"
                ? "dash_team_admin"
                : "dash_team_member",
          }
        : undefined;
      return {
        email: contact.email,
        role: canAssignRoles ? roleType : undefined,
        customMessage,
      };
    });

    dispatch({
      type: "attempt",
      payload: {
        attemptedEmails: requestInvitees.map((invitee) => invitee.email),
      },
    });
    setSubmissionStatus(SUBMISSION_STATUS_SUBMITTING);

    logDashTeamInvitePapEvent(PAP_Send_DashInviteModal, {
      eventState: "start",
    });

    callApiV2("dashGrowthTeamInvitesInviteUsers", {
      invitees: requestInvitees,
      platform: dashInvitePlatform,
      invite_source: inviteSource,
    })
      .then((response) => {
        if (!response.invite_result || response.invite_result.length === 0) {
          dispatch({
            type: "fail",
            payload: {
              error: {
                ".tag": "invite_to_dash_team_error_unspecified",
              },
            },
          });
          setSubmissionStatus(null);
          logDashTeamInvitePapEvent(PAP_Send_DashInviteModal, {
            eventState: "failed",
          });
        } else {
          const result = parseInviteUsersResponseInviteResult(
            response.invite_result,
          );
          dispatch({ type: "receive_response", payload: result });
          setSubmissionStatus(SUBMISSION_STATUS_SUBMITTED);
          logDashTeamInvitePapEvent(PAP_Send_DashInviteModal, {
            eventState: "success",
          });
        }
        return;
      })
      .catch(
        (
          e: DropboxResponseError<{
            error: dash_growth.InviteToDashTeamError;
          }>,
        ) => {
          // Sometimes the error is a string, sometimes it's an object
          // TODO: find out why it returns only a string in some cases
          const error: dash_growth.InviteToDashTeamError =
            e?.error?.error !== undefined
              ? e.error.error
              : {
                  ".tag": "invite_to_dash_team_error_unspecified",
                };
          logDashTeamInvitePapEvent(PAP_Send_DashInviteModal, {
            eventState: "failed",
          });
          dispatch({
            type: "fail",
            payload: {
              error,
            },
          });
          setSubmissionStatus(null);
          //TODO: log error
        },
      );
  }, [
    submissionStatus,
    customMessage,
    dashInvitePlatform,
    individualContacts,
    inviteSource,
    canAssignRoles,
    isValidForm,
    teamContacts,
    logDashTeamInvitePapEvent,
  ]);

  return {
    handleSubmit,
    submissionStatus,
    submissionResult: state,
  };
};
